import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { isEqual } from "es-toolkit/predicate"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AddressDialog from "@/modules/care-provider/advanced-search/address-dialog"
import { GeoStructuredAddressInput } from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchQuery.graphql"

export type AddressOption = GeoStructuredAddressInput & {
  label: string
}

interface CareProviderAddressSelectProps {
  value?: AddressOption | null
  options: AddressOption[]
  onChange?: (value: AddressOption | null) => void
}

export const CareProviderAddressSelect = ({ value, options, onChange }: CareProviderAddressSelectProps) => {
  const { t } = useTranslation()
  const [isOpenCareProviderAddressDialog, setIsOpenCareProviderAddressDialog] = useState(false)

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          asChild
          className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
          <Chip data-testid="address-filter-trigger" className="h-8 gap-0.5" checked={!!value} type="button">
            <div className={`overflow-hidden transition-all duration-200 ${value ? "w-5" : "w-0"}`}>
              <Check size={20} />
            </div>
            <span className="px-1.5">{value?.label}</span>
            <CaretDown size={20} />
          </Chip>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          data-testid="address-filter-content"
          className="absolute -top-1 grid gap-2 rounded-md border-none px-2 leading-5">
          <div className="grid gap-0.5 py-5">
            {options.map((item) => {
              const { label } = item
              const isSelected = isEqual(value, item)
              return (
                <DropdownMenuCheckboxItem
                  className="h-9"
                  key={label}
                  data-testid="address-option"
                  checked={isSelected}
                  onCheckedChange={() => onChange?.(item)}>
                  {label}
                </DropdownMenuCheckboxItem>
              )
            })}
            {options.length < 3 && (
              <DropdownMenuCheckboxItem
                onSelect={() => setIsOpenCareProviderAddressDialog(true)}
                className="text-primary hover:text-primary! h-9"
                key="add-manually"
                data-testid="add-manually-option"
                onCheckedChange={() => setIsOpenCareProviderAddressDialog(true)}>
                {t("admin.careProviders.advancedSearch.filter.addManually")}
              </DropdownMenuCheckboxItem>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {isOpenCareProviderAddressDialog ? (
        <AddressDialog
          onClose={() => setIsOpenCareProviderAddressDialog(false)}
          onAddressSearch={(address) => onChange?.(address)}
        />
      ) : null}
    </>
  )
}
